import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin } from "lucide-react";
import Logo from "./Logo";
import { WhatsAppIcon } from "./icons";
import { WHATSAPP_URL, scrollToId } from "./config";
import { EASE } from "./Reveal";

const LINKS = [
  { id: "beneficios", label: "Beneficios" },
  { id: "categorias", label: "Categorías" },
  { id: "opiniones", label: "Opiniones" },
  { id: "ubicacion", label: "Ubicación" },
  { id: "faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-3 sm:px-6">
      <motion.nav
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        className={`mx-auto flex max-w-7xl items-center justify-between rounded-full py-2.5 pl-5 pr-2.5 transition duration-300 ${
          scrolled
            ? "bg-cream/85 shadow-[0_10px_40px_-15px_rgba(44,32,28,0.25)] backdrop-blur-xl border border-linebr"
            : "bg-cream/40 backdrop-blur-md border border-transparent"
        }`}
      >
        <button
          data-testid="nav-logo-home"
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0)}
          className="shrink-0"
          aria-label="Ir al inicio"
        >
          <Logo />
        </button>

        <div className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="rounded-full px-4 py-2 text-sm font-semibold text-cocoa transition hover:bg-white hover:text-choco"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-whatsapp-cta"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-choco px-5 py-2.5 text-sm font-bold text-cream transition duration-300 hover:-translate-y-0.5 hover:bg-rose sm:inline-flex"
          >
            <WhatsAppIcon className="h-4 w-4" />
            Escribir por WhatsApp
          </a>
          <button
            data-testid="nav-toggle"
            onClick={() => setOpen(!open)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-linebr bg-white text-choco lg:hidden"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="mx-auto mt-2 max-w-7xl rounded-3xl border border-linebr bg-cream/95 p-4 shadow-xl backdrop-blur-xl lg:hidden"
          >
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="flex w-full items-center justify-between rounded-2xl px-4 py-3.5 text-left font-display text-lg font-semibold text-choco transition hover:bg-white"
              >
                {l.label}
                <MapPin className="h-4 w-4 text-sand" />
              </button>
            ))}
            <a
              data-testid="nav-mobile-whatsapp-cta"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-wagreen px-4 py-4 font-bold text-white"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Pedir por WhatsApp
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
